import {
  AreaChartVisualizationSettings,
  BarOrientation,
  BarChartVisualizationSettings,
  BarThickness,
  GaugeVisualizationSettings,
  ColoredThreshold,
  LineChartVisualizationSettings,
  PieChartVisualizationSettings,
  QuerySettingsDto,
  TableVisualizationSettings,
  ValueVisualizationSettings,
  WidgetSettingsDto,
  WidgetSettingsType,
} from '@thingslog/repositories';

type VisualizationSettings =
  | LineChartVisualizationSettings
  | BarChartVisualizationSettings
  | PieChartVisualizationSettings
  | AreaChartVisualizationSettings
  | GaugeVisualizationSettings
  | ValueVisualizationSettings
  | TableVisualizationSettings;

const HEX_COLOR_PATTERN = /^#[0-9A-Fa-f]{6}$/;
const DEFAULT_GAUGE_COLOR = '#3B82F6';
const DEFAULT_GAUGE_BAR_ORIENTATION: BarOrientation = 'horizontal';
const DEFAULT_GAUGE_BAR_THICKNESS: BarThickness = 'regular';
const DEFAULT_VALUE_COLOR = '#3B82F6';
const DEFAULT_THRESHOLDS: ColoredThreshold[] = [
  { value: 70, color: '#F59E0B', label: 'Warning' },
  { value: 90, color: '#EF4444', label: 'Critical' },
];

const isHexColor = (color: string | undefined): color is string => {
  return typeof color === 'string' && HEX_COLOR_PATTERN.test(color);
};

const isBarOrientation = (orientation: string | undefined): orientation is BarOrientation => {
  return orientation === 'vertical' || orientation === 'horizontal';
};

const isBarThickness = (thickness: string | undefined): thickness is BarThickness => {
  return thickness === 'slim' || thickness === 'regular' || thickness === 'wide';
};

export class WidgetSettingsFactory {
  public static create(props: MutateProps): WidgetSettingsDto {
    const base = {
      type: props.type,
      title: props.title,
      query: props.query,
      dataSourceId: props.dataSourceId,
    };

    return {
      ...base,
      visualization: WidgetSettingsFactory.createDefaultVisualization(props.type),
    } as WidgetSettingsDto;
  }

  public static copy(
    source: WidgetSettingsDto,
    overrides: Partial<WidgetSettingsDto> = {}
  ): WidgetSettingsDto {
    return WidgetSettingsFactory.normalize({
      ...source,
      ...overrides,
    } as WidgetSettingsDto);
  }

  public static normalize(settings: WidgetSettingsDto): WidgetSettingsDto {
    const settingsWithNormalizedQuery = {
      ...settings,
      query: WidgetSettingsFactory.normalizeQuerySettings(settings.query),
    } as WidgetSettingsDto;

    const visualization = {
      ...settingsWithNormalizedQuery,
      visualization: {
        ...WidgetSettingsFactory.createDefaultVisualization(settingsWithNormalizedQuery.type),
        ...settingsWithNormalizedQuery.visualization,
      },
    } as WidgetSettingsDto;

    if (visualization.type === 'gauge') {
      return {
        ...visualization,
        visualization: WidgetSettingsFactory.normalizeGaugeVisualization(
          visualization.visualization
        ),
      } as WidgetSettingsDto;
    }

    if (visualization.type === 'value') {
      return {
        ...visualization,
        visualization: WidgetSettingsFactory.normalizeValueVisualization(
          visualization.visualization
        ),
      } as WidgetSettingsDto;
    }

    return visualization;
  }

  private static normalizeQuerySettings(query: QuerySettingsDto): QuerySettingsDto {
    if (query.type === 'api-devices-signal') {
      return {
        ...query,
        metrics: query.metrics?.length ? query.metrics : ['level'],
      };
    }

    if (query.type === 'api-devices-transmissions') {
      return {
        ...query,
        metrics: query.metrics?.length ? query.metrics : ['days'],
      };
    }

    return query;
  }

  public static createDefaultVisualization(type: WidgetSettingsType): VisualizationSettings {
    switch (type) {
      case 'line-chart':
        return {
          ...WidgetSettingsFactory.createDefaultLineSeriesVisualization(),
          showPoints: false,
        };
      case 'area-chart':
        return {
          ...WidgetSettingsFactory.createDefaultLineSeriesVisualization(),
          stacked: false,
          fillOpacity: 'balanced',
        };
      case 'bar-chart':
        return {
          ...WidgetSettingsFactory.createDefaultCartesianVisualization(),
          orientation: 'vertical',
          grouping: 'grouped',
          barThickness: 'regular',
        };
      case 'pie-chart':
        return {
          showLegend: true,
          renderLegendAside: false,
          shape: 'pie',
        };
      case 'gauge':
        return {
          visualizationType: 'dial',
          barOrientation: DEFAULT_GAUGE_BAR_ORIENTATION,
          barThickness: DEFAULT_GAUGE_BAR_THICKNESS,
          minValue: 0,
          maxValue: 100,
          color: DEFAULT_GAUGE_COLOR,
          thresholds: DEFAULT_THRESHOLDS.map((threshold: ColoredThreshold) => ({
            ...threshold,
          })),
          showValue: true,
          showMinMaxLabels: true,
          showLegend: false,
          showLegendAside: false,
        };
      case 'value':
        return {
          color: DEFAULT_VALUE_COLOR,
          thresholds: DEFAULT_THRESHOLDS.map((threshold: ColoredThreshold) => ({
            ...threshold,
          })),
          prefix: '',
          suffix: '',
          stacked: false,
          compact: false,
          showTargetPercentage: false,
        };
      case 'table':
        return {
          paginated: true,
        };
      default:
        throw new Error('Invalid widget type');
    }
  }

  private static createDefaultCartesianVisualization(): {
    showGrid: boolean;
    showLegend: boolean;
    renderLegendAside: boolean;
    showXAxisLabels: boolean;
    showYAxisLabels: boolean;
  } {
    return {
      showGrid: true,
      showLegend: true,
      renderLegendAside: false,
      showXAxisLabels: true,
      showYAxisLabels: true,
    };
  }

  private static createDefaultLineSeriesVisualization(): {
    showGrid: boolean;
    showLegend: boolean;
    renderLegendAside: boolean;
    showXAxisLabels: boolean;
    showYAxisLabels: boolean;
    lineThickness: 'fine' | 'standard' | 'bold';
    interpolation: 'straight' | 'smooth' | 'stepped';
  } {
    return {
      ...WidgetSettingsFactory.createDefaultCartesianVisualization(),
      lineThickness: 'standard',
      interpolation: 'straight',
    };
  }

  private static normalizeGaugeVisualization(
    visualization: GaugeVisualizationSettings
  ): GaugeVisualizationSettings {
    return {
      ...visualization,
      barOrientation:
        visualization.visualizationType === 'bar' && isBarOrientation(visualization.barOrientation)
          ? visualization.barOrientation
          : DEFAULT_GAUGE_BAR_ORIENTATION,
      barThickness:
        visualization.visualizationType === 'bar' && isBarThickness(visualization.barThickness)
          ? visualization.barThickness
          : DEFAULT_GAUGE_BAR_THICKNESS,
      color: isHexColor(visualization.color) ? visualization.color : DEFAULT_GAUGE_COLOR,
      thresholds: WidgetSettingsFactory.normalizeThresholds(visualization.thresholds),
    };
  }

  private static normalizeValueVisualization(
    visualization: ValueVisualizationSettings
  ): ValueVisualizationSettings {
    return {
      ...visualization,
      color: isHexColor(visualization.color) ? visualization.color : DEFAULT_VALUE_COLOR,
      thresholds: WidgetSettingsFactory.normalizeThresholds(visualization.thresholds),
    };
  }

  private static normalizeThresholds(thresholds: ColoredThreshold[]): ColoredThreshold[] {
    const safeThresholds = Array.isArray(thresholds) ? thresholds : DEFAULT_THRESHOLDS;

    return safeThresholds.map((threshold: ColoredThreshold, index: number): ColoredThreshold => {
      const defaultThreshold = DEFAULT_THRESHOLDS[index] ?? DEFAULT_THRESHOLDS[0];

      return {
        ...defaultThreshold,
        ...threshold,
        color: isHexColor(threshold.color) ? threshold.color : defaultThreshold.color,
        label: threshold.label ?? defaultThreshold.label,
      };
    });
  }
}

interface MutatePropBase {
  type: WidgetSettingsType;
  title: string;
  query: QuerySettingsDto;
  dataSourceId: string;
}

interface MutatePropsLineChart extends MutatePropBase {
  type: 'line-chart';
}

interface MutatePropsBarChart extends MutatePropBase {
  type: 'bar-chart';
}

interface MutatePropsPieChart extends MutatePropBase {
  type: 'pie-chart';
}

interface MutatePropsAreaChart extends MutatePropBase {
  type: 'area-chart';
}

interface MutatePropsGauge extends MutatePropBase {
  type: 'gauge';
}

interface MutatePropsValue extends MutatePropBase {
  type: 'value';
}

interface MutatePropsTable extends MutatePropBase {
  type: 'table';
}

type MutateProps =
  | MutatePropsLineChart
  | MutatePropsBarChart
  | MutatePropsPieChart
  | MutatePropsAreaChart
  | MutatePropsGauge
  | MutatePropsValue
  | MutatePropsTable;
